import React, { PureComponent } from 'react';
import T from 'i18n-react';
import { connect } from '../../../../redux/connect';
import {
  changeLocationAction,
  setQueryParamsAction,
  fetchIssuesReport,
  clearSearchResultsAction,
  getSearchResultsAction,
  trackEvent,
} from '../../../../redux/actions';
import {
  getQueryParams,
  getIssuesCollectionLoading,
  getReportContainerId,
  isAdmin,
  isFetchReportLoading,
  getIssuesCollectionSearchResults,
  getProjectId,
} from '../../../../redux/selectors';
import featureFlags, { FEATURE_FLAGS } from '../../../../utils/featureFlags';
import ProjectViewExport from './components/ProjectViewExport';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { SearchBoxDropdown } from '@adsk/bim360-matrix-react-components';
import { createSearchQuery } from '../../../../utils/query-helpers';
import { ISSUE_FILTER_PROPERTIES, PAGE_PARAMS } from '../../../../constants/issue-query-params';
import { searchRfi } from '../../../../utils/analytics-helpers';
import FilterIcon from './components/FilterIcon';
import FilterIndicator from './components/FilterIndicator';
import Filter from '../../../../components/Filter';


export class PureProjectGridHeader extends PureComponent {

  constructor(props) {
    super(props);
    this.state = {
      showFilter: false,
    };

    this.handleFilterDropdown = this.handleFilterDropdown.bind(this);
  }

  handleFilterDropdown(state) {
    this.setState({ showFilter: state });
  }

  toggleFilterDropdown() {
    this.setState({ showFilter: !this.state.showFilter });
  }

  onSearch = (searchExpression) => {
    searchExpression = searchExpression.trim();
    const searchQuery = searchExpression ? createSearchQuery(searchExpression) : '';
    if (searchQuery) {
      this.props.trackEvent(searchRfi());
    }
    this.props.updateSearchExpression(searchExpression);
    this.props.setQueryParamsAction({ [ISSUE_FILTER_PROPERTIES.SEARCH]: searchQuery, [PAGE_PARAMS.OFFSET]: 0 });
  };

  handleSuggestedClick = (attributes) => {
    const { changeLocationAction } = this.props;
    changeLocationAction(`/project/rfis/${attributes.id}`);
  };

  getSearchSuggestedSearchResults = (searchExpression) => {
    this.props.updateSearchExpression(searchExpression);
    searchExpression = searchExpression.trim();
    if (searchExpression) {
      const searchQuery = createSearchQuery(searchExpression);
      this.props.getSearchResultsAction(searchQuery);
    } else if (this.props.searchResults && this.props.searchResults.length) {
      this.props.clearSearchResultsAction();
    }
  };

  getSuggestionIdentifier = (suggestion) => {
    return suggestion.customIdentifier;
  };

  suggestionDataGetter = (suggestion) => {
    const identifier = this.getSuggestionIdentifier(suggestion);
    if (identifier) {
      return `#${identifier} - ${suggestion.title}`;
    } else {
      return suggestion.title;
    }
  };

  suggestionPrefixRenderer = (suggestion) => {
    const identifier = this.getSuggestionIdentifier(suggestion);
    if (identifier) {
      // If we have identifier then we don't need a prefix component since the identifier is passed as part of the data in the dataGetter.
      // Therefore we're returning undefined (so the prop itself will be undefined).
      return undefined;
    } else {
      return <span className="ProjectGridHeader__search-box-suggested-result-unspecified-prefix">
        {`#${T.translate('unspecifiedValue')} -`}
      </span>;
    }
  };

  renderSearchBox() {
    const searchResults = this.props.searchResults || [];
    return <SearchBoxDropdown
      className={cx('ProjectGridHeader__search-box-container', 'ProjectGridHeader__search-box-container--narrow')}
      placeholder={T.translate('searchRfi')}
      onSearch={this.onSearch}
      searchExpression={this.props.searchExpression}
      searchForSuggestedResults={this.getSearchSuggestedSearchResults}
      onSuggestedResultRowSelect={this.handleSuggestedClick}
      suggestionList={searchResults}
      suggestionDataGetter={this.suggestionDataGetter}
      suggestionPrefixRenderer={this.suggestionPrefixRenderer}
    />;
  }

  renderPopupFilterHeader() {
    const baseFilterToggleButtonClass = 'isui-filter-sort__show-filter';
    const filterToggleButtonClass = cx(baseFilterToggleButtonClass, 'isui-filter-sort__show-filter--isuipv', {
      'isui-filter-sort__show-filter--open': this.state.showFilter,
    });

    return <React.Fragment>
      {this.props.searchRfisEnabled ? this.renderSearchBox() : ""}
      <div className="ProjectGridHeader__left-buttons">
        <span className={filterToggleButtonClass} onClick={() => this.toggleFilterDropdown()}>{T.translate('filter.filter')}</span>
        <div className={this.props.searchRfisEnabled ? 'ProjectGrid__filter-sort-with-search-container' : 'ProjectGrid__filter-sort-container'}>
          <Filter
            visible={this.state.showFilter}
            disabled={false}
            displayFilterDropdown={this.handleFilterDropdown}
            outsideClickIgnoreClass={baseFilterToggleButtonClass}
          />
        </div>
      </div>
    </React.Fragment>;
  }

  renderDrawerFilterHeader() {
    const { filtersOpen, toggleFiltersOpen, isFiltered, resetFilters } = this.props;
    return (
      <React.Fragment>
        <FilterIcon isFilterOpen={filtersOpen} toggleFilterPanel={() => { toggleFiltersOpen(!filtersOpen); }} />
        {this.props.searchRfisEnabled ? this.renderSearchBox() : ""}
        {isFiltered && <FilterIndicator clearFilters={resetFilters} text={T.translate('filter.isOn')} onClick={() => { toggleFiltersOpen(true); }} />}
      </React.Fragment>);
  }

  render() {
    const { createBtn, isFetchReportLoading, loading, canExportPDF,
      fetchIssuesReport, showExportPdfModal } = this.props;

    const exportCSVLabel = canExportPDF ? T.translate('export.csvReport') : T.translate('export.export');

    const exportOptions = [
      { eventKey: 2, label: exportCSVLabel, handleOnClick: () => fetchIssuesReport() },
    ];

    if (canExportPDF) {
      exportOptions.unshift({ eventKey: 1, label: T.translate('export.pdfReport'), handleOnClick: () => showExportPdfModal() });
    }

    return (
      <div className="ProjectGridHeader" data-testid="ProjectGridHeader">
        <div className="ProjectGridHeader__search-wrapper">
          {this.renderDrawerFilterHeader()}
        </div>
        <div className="ProjectGridHeader__right-buttons">
          <ProjectViewExport
            exportOptions={exportOptions}
            disabled={isFetchReportLoading || loading}
            exportingText={T.translate('export.exporting')}
            isFetchReportLoading={isFetchReportLoading}
          />
          {createBtn}
        </div>
      </div>
    );
  }
}

PureProjectGridHeader.displayName = 'ProjectGridHeader';

PureProjectGridHeader.propTypes = {
  changeLocationAction: PropTypes.func.isRequired,
  setQueryParamsAction: PropTypes.func.isRequired,
  queryParams: PropTypes.object,
  createBtn: PropTypes.node,
  isFetchReportLoading: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  canExportPDF: PropTypes.bool.isRequired,
  fetchIssuesReport: PropTypes.func.isRequired,
  showExportPdfModal: PropTypes.func.isRequired,
  getSearchResultsAction: PropTypes.func.isRequired,
  searchResults: PropTypes.array,
  clearSearchResultsAction: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  trackEvent: PropTypes.func.isRequired,
  updateSearchExpression: PropTypes.func.isRequired,
  searchExpression: PropTypes.string,
  searchRfisEnabled: PropTypes.bool,
  filtersOpen: PropTypes.bool,
  toggleFiltersOpen: PropTypes.func,
  resetFilters: PropTypes.func,
  isFiltered: PropTypes.bool,
};

const mapStateToProps = (state) => {
  const isUserAdmin = isAdmin(state);
  const hasReportContainerId = !!getReportContainerId(state);

  return {
    projectId: getProjectId(state),
    queryParams: getQueryParams(state),
    loading: getIssuesCollectionLoading(state),
    canExportPDF: isUserAdmin && hasReportContainerId,
    isFetchReportLoading: isFetchReportLoading(state),
    searchResults: getIssuesCollectionSearchResults(state),
    searchRfisEnabled: featureFlags.get(FEATURE_FLAGS.RFIS_SEARCH),
  };
};

export default connect(mapStateToProps, {
  changeLocationAction,
  setQueryParamsAction,
  fetchIssuesReport,
  clearSearchResultsAction,
  getSearchResultsAction,
  trackEvent,
})(PureProjectGridHeader);

